// PlantWorkOrders.tsx
// Plant & Equipment Maintenance Work Orders
// Reads open defects from the shared _equipmentDefects store (populated by
// PlantPreStartChecks → addEquipmentDefect) and allows the Plant Manager /
// maintenance officer to raise and close Work Orders against each defect.
//
// Pipeline:
//   PlantPreStartChecks (FAIL) → addEquipmentDefect() → _equipmentDefects store
//   PlantWorkOrders reads getOpenEquipmentDefects()
//   PM raises WO → assigns technician, sets target date, records cost
//   WO completed → closeEquipmentDefect() → asset cleared for allocation
//
// WHS Regulation 2011 (Qld) Part 5.1 — Plant must not be used while a
// known defect has not been rectified.

import { useState, useMemo } from 'react'
import { useAuthStore }          from '../stores/auth.store'
import {
  MOCK_EQUIPMENT,
  getOpenEquipmentDefects,
  closeEquipmentDefect,
  type EquipmentDefectRecord,
} from '../utils/mockDataExtended'
import { MOCK_EMPLOYEES } from '../utils/mockDataExtended'
import {
  Wrench, Plus, CheckCircle, AlertTriangle, Clock,
  X, Search, ChevronRight, AlertOctagon, FileText,
} from 'lucide-react'

// ── Work Order types ──────────────────────────────────────────────

type WOStatus   = 'OPEN' | 'IN_PROGRESS' | 'COMPLETED' | 'DEFERRED'
type WOPriority = 'ROUTINE' | 'URGENT' | 'CRITICAL'

interface WorkOrder {
  id:            string
  woNumber:      string
  defectId:      string | null   // null for manually-raised WOs
  assetId:       string
  assetNumber:   string
  assetName:     string
  description:   string
  priority:      WOPriority
  status:        WOStatus
  assignedToId:  string | null
  assignedToName:string | null
  raisedById:    string
  raisedByName:  string
  raisedAt:      string
  targetDate:    string | null
  completedAt:   string | null
  labourHours:   number | null
  partsCost:     number | null
  notes:         string
  completionNotes: string
}

// ── Module-level WO store ─────────────────────────────────────────

const _workOrders: WorkOrder[] = [
  {
    id:            'wo-001',
    woNumber:      'WO-2026-001',
    defectId:      'def-ps-002',   // matches seed defect from PlantPreStart
    assetId:       'eq5',
    assetNumber:   'PLT-001',
    assetName:     'Kubota KX016-4 Excavator',
    description:   'Pre-start 080826: Hydraulic oil below minimum on dipstick. Possible slow leak at boom cylinder.',
    priority:      'URGENT',
    status:        'IN_PROGRESS',
    assignedToId:  'e4',
    assignedToName:'Pete Hamill (Plant)',
    raisedById:    'u2',
    raisedByName:  'Jane Smith',
    raisedAt:      new Date(Date.now() - 8 * 3600000).toISOString(),
    targetDate:    new Date(Date.now() + 1 * 86400000).toISOString().split('T')[0],
    completedAt:   null,
    labourHours:   null,
    partsCost:     null,
    notes:         'Asset removed from service pending inspection. Do not allocate until WO closed.',
    completionNotes: '',
  },
]

let _woSeq = 2

function addWorkOrder(wo: Omit<WorkOrder, 'id' | 'woNumber'>): WorkOrder {
  const newWO: WorkOrder = {
    ...wo,
    id:       `wo-${Date.now()}`,
    woNumber: `WO-2026-${String(_woSeq++).padStart(3, '0')}`,
  }
  _workOrders.push(newWO)
  return newWO
}

function closeWorkOrder(
  id: string,
  completionNotes: string,
  labourHours: number | null,
  partsCost: number | null,
): void {
  const wo = _workOrders.find(w => w.id === id)
  if (!wo) return
  wo.status           = 'COMPLETED'
  wo.completedAt      = new Date().toISOString()
  wo.completionNotes  = completionNotes
  wo.labourHours      = labourHours
  wo.partsCost        = partsCost
  // Close the linked defect so the asset is cleared for allocation
  if (wo.defectId) {
    closeEquipmentDefect(wo.defectId)
  }
}

// ── Helpers ───────────────────────────────────────────────────────

const fmt   = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'
const fmtDT = (s?: string | null) =>
  s ? new Date(s).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
const NOW = new Date().toISOString()

const PRIORITY_META: Record<WOPriority, { label: string; bg: string; text: string; dot: string }> = {
  ROUTINE:  { label: 'Routine',  bg: 'bg-gray-100',  text: 'text-gray-600',  dot: 'bg-gray-400'  },
  URGENT:   { label: 'Urgent',   bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  CRITICAL: { label: 'Critical', bg: 'bg-red-100',   text: 'text-red-700',   dot: 'bg-red-500'   },
}

const STATUS_META: Record<WOStatus, { label: string; bg: string; text: string }> = {
  OPEN:        { label: 'Open',        bg: 'bg-blue-100',  text: 'text-blue-700'  },
  IN_PROGRESS: { label: 'In progress', bg: 'bg-amber-100', text: 'text-amber-700' },
  COMPLETED:   { label: 'Completed',   bg: 'bg-green-100', text: 'text-green-700' },
  DEFERRED:    { label: 'Deferred',    bg: 'bg-gray-100',  text: 'text-gray-500'  },
}

// ── Create WO modal ───────────────────────────────────────────────

function CreateWOModal({
  defect, onClose, onCreated, currentUserName,
}: {
  defect: EquipmentDefectRecord | null
  onClose: () => void
  onCreated: (wo: WorkOrder) => void
  currentUserName: string
}) {
  const asset = defect ? MOCK_EQUIPMENT.find(e => e.id === defect.equipmentId) : null

  const [assetId, setAssetId]   = useState(defect?.equipmentId ?? MOCK_EQUIPMENT[0].id)
  const [desc, setDesc]         = useState(defect?.description ?? '')
  const [priority, setPriority] = useState<WOPriority>(
    defect?.severity === 'CRITICAL' ? 'CRITICAL' : defect?.severity === 'MAJOR' ? 'URGENT' : 'ROUTINE'
  )
  const [assignedId, setAssignedId] = useState('')
  const [targetDate, setTargetDate] = useState(
    new Date(Date.now() + 2 * 86400000).toISOString().split('T')[0]
  )
  const [notes, setNotes] = useState('')

  const selectedAsset = MOCK_EQUIPMENT.find(e => e.id === assetId)!
  const assignableTechs = MOCK_EMPLOYEES.filter(e => e.trade !== 'ADMINISTRATION')

  const handleCreate = () => {
    const assignee = assignableTechs.find(e => e.id === assignedId)
    const wo = addWorkOrder({
      defectId:       defect?.id ?? null,
      assetId,
      assetNumber:    selectedAsset.assetNumber,
      assetName:      selectedAsset.name,
      description:    desc,
      priority,
      status:         'OPEN',
      assignedToId:   assignedId || null,
      assignedToName: assignee ? `${assignee.firstName} ${assignee.lastName}` : null,
      raisedById:     'u2',
      raisedByName:   currentUserName,
      raisedAt:       NOW,
      targetDate:     targetDate || null,
      completedAt:    null,
      labourHours:    null,
      partsCost:      null,
      notes,
      completionNotes: '',
    })
    onCreated(wo)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-amber-50">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-amber-600" />Create maintenance work order
            </h2>
            {defect && <p className="text-xs text-gray-500 mt-0.5">From defect: {defect.assetNumber}</p>}
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          {!defect && (
            <div>
              <label className="label-sm">Asset</label>
              <select value={assetId} onChange={e => setAssetId(e.target.value)} className="input text-sm w-full">
                {MOCK_EQUIPMENT.filter(e => e.status !== 'DISPOSED').map(e => (
                  <option key={e.id} value={e.id}>{e.assetNumber} — {e.name}</option>
                ))}
              </select>
            </div>
          )}
          {defect && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-800">
              <AlertTriangle className="w-4 h-4 inline mr-1.5 text-red-600" />
              <strong>Open defect:</strong> {defect.description}
              <div className="text-xs text-red-600 mt-1">
                Severity: {defect.severity} · Reported: {fmtDT(defect.reportedAt)}
              </div>
            </div>
          )}
          <div>
            <label className="label-sm">Work description <span className="text-red-500">*</span></label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3}
              placeholder="Describe the work to be performed…"
              className="input text-sm w-full resize-none mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-sm">Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value as WOPriority)} className="input text-sm w-full">
                <option value="ROUTINE">Routine</option>
                <option value="URGENT">Urgent</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>
            <div>
              <label className="label-sm">Target completion</label>
              <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} className="input text-sm w-full" />
            </div>
          </div>
          <div>
            <label className="label-sm">Assign to technician</label>
            <select value={assignedId} onChange={e => setAssignedId(e.target.value)} className="input text-sm w-full">
              <option value="">— Unassigned —</option>
              {MOCK_EMPLOYEES.map(e => (
                <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-sm">Notes</label>
            <input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any additional instructions for the technician…"
              className="input text-sm w-full" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={handleCreate} disabled={!desc.trim()}
              className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40">
              <Wrench className="w-4 h-4" />Create work order
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Close WO modal ────────────────────────────────────────────────

function CloseWOModal({ wo, onClose, onClosed }: {
  wo: WorkOrder; onClose: () => void; onClosed: () => void
}) {
  const [notes, setNotes]   = useState('')
  const [hours, setHours]   = useState<string>('')
  const [parts, setParts]   = useState<string>('')

  const handleClose = () => {
    closeWorkOrder(wo.id, notes, hours ? +hours : null, parts ? +parts : null)
    onClosed()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-green-50">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />Complete work order — {wo.woNumber}
          </h2>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm">
            <div className="font-medium text-gray-900">{wo.assetNumber} — {wo.assetName}</div>
            <div className="text-xs text-gray-500 mt-0.5">{wo.description}</div>
          </div>
          <div>
            <label className="label-sm">Completion notes <span className="text-red-500">*</span></label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="Describe what was done, parts replaced, outcome…"
              className="input text-sm w-full resize-none mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-sm">Labour hours</label>
              <input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)}
                placeholder="0.0" className="input text-sm w-full" />
            </div>
            <div>
              <label className="label-sm">Parts cost (ex GST)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input type="number" step="0.01" value={parts} onChange={e => setParts(e.target.value)}
                  placeholder="0.00" className="input pl-7 text-sm w-full" />
              </div>
            </div>
          </div>
          {wo.defectId && (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-800">
              <CheckCircle className="w-4 h-4 inline mr-1.5 text-green-600" />
              Completing this WO will <strong>close the linked defect</strong> and clear the asset for allocation.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={handleClose} disabled={!notes.trim()}
              className="btn-primary text-sm flex items-center gap-2 disabled:opacity-40">
              <CheckCircle className="w-4 h-4" />Complete & close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────

export function PlantWorkOrders() {
  const { isTier, user } = useAuthStore()
  const canManage = isTier('B_PM') || isTier('B_PC') || isTier('A_DEVELOPER')

  const [wos, setWos]               = useState<WorkOrder[]>([..._workOrders])
  const [showCreate, setShowCreate] = useState(false)
  const [selectedDefect, setSelectedDefect] = useState<EquipmentDefectRecord | null>(null)
  const [selected, setSelected]     = useState<WorkOrder | null>(null)
  const [showClose, setShowClose]   = useState(false)
  const [search, setSearch]         = useState('')
  const [filterStatus, setFilterStatus] = useState<WOStatus | 'ALL'>('ALL')
  const [tick, setTick]             = useState(0)  // force re-read of store after close

  // Live open defects from store (includes defects from PlantPreStartChecks)
  const openDefects = useMemo(() => getOpenEquipmentDefects(), [tick])

  const filtered = useMemo(() => wos.filter(w => {
    const ms = !search || w.woNumber.toLowerCase().includes(search.toLowerCase())
                       || w.assetNumber.toLowerCase().includes(search.toLowerCase())
                       || w.description.toLowerCase().includes(search.toLowerCase())
    const mf = filterStatus === 'ALL' || w.status === filterStatus
    return ms && mf
  }), [wos, search, filterStatus])

  const openCount   = wos.filter(w => w.status === 'OPEN').length
  const activeCount = wos.filter(w => w.status === 'IN_PROGRESS').length
  const doneCount   = wos.filter(w => w.status === 'COMPLETED').length

  const handleCreated = (wo: WorkOrder) => {
    setWos([...wos, wo])
  }

  const handleClosed = () => {
    setWos([..._workOrders])
    setSelected(null)
    setTick(t => t + 1)
  }

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-[#2E75B6]" />
            Plant Work Orders
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Maintenance work orders raised from pre-start defects · WHS Reg 2011 (Qld) Part 5.1
          </p>
        </div>
        {canManage && (
          <button onClick={() => { setSelectedDefect(null); setShowCreate(true) }}
            className="btn-primary text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" />New work order
          </button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Open WOs',        value: openCount,        color: openCount > 0 ? 'text-blue-700' : 'text-gray-400',   sub: 'awaiting action' },
          { label: 'In progress',     value: activeCount,      color: activeCount > 0 ? 'text-amber-700' : 'text-gray-400', sub: 'work underway' },
          { label: 'Open defects',    value: openDefects.length, color: openDefects.length > 0 ? 'text-red-600' : 'text-gray-400', sub: 'across all assets' },
          { label: 'Completed',       value: doneCount,        color: 'text-green-700',                                     sub: 'this session' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {/* Open defects not yet having a WO */}
      {openDefects.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
            <AlertOctagon className="w-4 h-4 text-red-600" />
            {openDefects.length} open defect{openDefects.length !== 1 ? 's' : ''} — assets blocked from allocation
          </div>
          {openDefects.map(d => {
            const hasWO = wos.some(w => w.defectId === d.id && w.status !== 'COMPLETED')
            const asset = MOCK_EQUIPMENT.find(e => e.id === d.equipmentId)
            return (
              <div key={d.id} className="flex items-center gap-3 bg-white border border-red-100 rounded-lg px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">{d.assetNumber} — {asset?.name ?? 'Unknown asset'}</div>
                  <div className="text-xs text-red-600 mt-0.5">{d.description}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {d.severity} · {fmtDT(d.reportedAt)}
                    {hasWO && <span className="ml-2 text-amber-600">WO raised</span>}
                  </div>
                </div>
                {!hasWO && canManage && (
                  <button
                    onClick={() => { setSelectedDefect(d); setShowCreate(true) }}
                    className="btn-primary text-xs flex items-center gap-1 flex-shrink-0">
                    <Wrench className="w-3 h-3" />Raise WO
                  </button>
                )}
                {hasWO && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0">WO in progress</span>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search WO, asset, or description…"
            className="input pl-9 text-sm w-full" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)} className="input text-sm w-40">
          <option value="ALL">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="IN_PROGRESS">In progress</option>
          <option value="COMPLETED">Completed</option>
          <option value="DEFERRED">Deferred</option>
        </select>
      </div>

      {/* WO list + detail */}
      <div className="flex gap-5 items-start">
        <div className={`${selected ? 'w-1/2' : 'w-full'} space-y-2 transition-all`}>
          {filtered.map(wo => {
            const pm  = PRIORITY_META[wo.priority]
            const sm  = STATUS_META[wo.status]
            const isSel = selected?.id === wo.id
            const isOverdue = wo.targetDate && new Date(wo.targetDate) < new Date() && wo.status !== 'COMPLETED'
            return (
              <div key={wo.id}
                onClick={() => setSelected(isSel ? null : wo)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${
                  isSel ? 'border-[#2E75B6] ring-2 ring-blue-100'
                  : isOverdue ? 'border-red-300'
                  : wo.priority === 'CRITICAL' && wo.status !== 'COMPLETED' ? 'border-red-200'
                  : 'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{wo.woNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${pm.bg} ${pm.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${pm.dot}`} />{pm.label}
                      </span>
                      {isOverdue && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">OVERDUE</span>}
                    </div>
                    <div className="font-semibold text-sm text-gray-900">{wo.assetNumber} — {wo.assetName}</div>
                    <div className="text-xs text-gray-600 mt-0.5 line-clamp-1">{wo.description}</div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400 flex-wrap">
                      {wo.assignedToName && <span>Assigned: {wo.assignedToName}</span>}
                      {wo.targetDate && <span>Due: {fmt(wo.targetDate)}</span>}
                      <span>Raised: {fmtDT(wo.raisedAt)}</span>
                      {wo.completedAt && <span className="text-green-600">Completed: {fmtDT(wo.completedAt)}</span>}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSel ? 'text-[#2E75B6]' : 'text-gray-400'}`} />
                </div>
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">
              No work orders match the current filter
            </div>
          )}
        </div>

        {selected && (
          <div className="w-1/2 sticky top-6 space-y-3" style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-mono text-gray-400">{selected.woNumber}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_META[selected.status].bg} ${STATUS_META[selected.status].text}`}>
                      {STATUS_META[selected.status].label}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_META[selected.priority].bg} ${PRIORITY_META[selected.priority].text}`}>
                      {PRIORITY_META[selected.priority].label}
                    </span>
                  </div>
                  <div className="font-semibold text-sm text-gray-900">{selected.assetNumber} — {selected.assetName}</div>
                </div>
                <button onClick={() => setSelected(null)}><X className="w-4 h-4 text-gray-400" /></button>
              </div>

              <div className="p-4 space-y-4 text-sm">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Description</div>
                  <p className="text-gray-800">{selected.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><span className="text-gray-400">Assigned to</span><div className="font-medium text-gray-800 mt-0.5">{selected.assignedToName ?? 'Unassigned'}</div></div>
                  <div><span className="text-gray-400">Target date</span><div className="font-medium text-gray-800 mt-0.5">{fmt(selected.targetDate)}</div></div>
                  <div><span className="text-gray-400">Raised by</span><div className="font-medium text-gray-800 mt-0.5">{selected.raisedByName}</div></div>
                  <div><span className="text-gray-400">Raised at</span><div className="font-medium text-gray-800 mt-0.5">{fmtDT(selected.raisedAt)}</div></div>
                  {selected.labourHours && <div><span className="text-gray-400">Labour hours</span><div className="font-medium text-gray-800 mt-0.5">{selected.labourHours}h</div></div>}
                  {selected.partsCost && <div><span className="text-gray-400">Parts cost</span><div className="font-medium text-gray-800 mt-0.5">${selected.partsCost.toFixed(2)}</div></div>}
                </div>

                {selected.notes && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Notes</div>
                    <p className="text-gray-700 bg-gray-50 rounded-lg p-3 text-sm">{selected.notes}</p>
                  </div>
                )}

                {selected.completionNotes && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Completion notes</div>
                    <p className="text-gray-700 bg-green-50 rounded-lg p-3 text-sm">{selected.completionNotes}</p>
                  </div>
                )}

                {selected.defectId && (
                  <div className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
                    Linked defect: <span className="font-mono">{selected.defectId}</span>
                    {selected.status === 'COMPLETED'
                      ? <span className="ml-2 text-green-600 font-medium">✓ Defect closed — asset cleared for allocation</span>
                      : <span className="ml-2 text-amber-600">Asset blocked until this WO is completed</span>}
                  </div>
                )}
              </div>

              {['OPEN', 'IN_PROGRESS'].includes(selected.status) && canManage && (
                <div className="border-t px-4 py-3 bg-green-50 flex gap-2 items-center flex-shrink-0">
                  <span className="flex-1 text-xs text-green-800">
                    {selected.defectId
                      ? 'Completing this WO will close the linked defect and clear the asset for allocation.'
                      : 'Mark this work order as complete.'}
                  </span>
                  <button onClick={() => setShowClose(true)}
                    className="btn-primary text-xs flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" />Complete WO
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateWOModal
          defect={selectedDefect}
          onClose={() => { setShowCreate(false); setSelectedDefect(null) }}
          onCreated={handleCreated}
          currentUserName={user?.fullName ?? 'PM'}
        />
      )}

      {showClose && selected && (
        <CloseWOModal
          wo={selected}
          onClose={() => setShowClose(false)}
          onClosed={handleClosed}
        />
      )}
    </div>
  )
}
